import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import '../../../../feature-styles/auth.css';
import {
  FaShieldAlt,
  FaKey,
  FaIdCard,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
  FaRedo,
  FaSignInAlt,
} from 'react-icons/fa';
import { onboardingApi } from '../../api/onboarding.api';
import { onboardingSchema } from '../../../../schemas/onboarding.schema';
import { useRenapo } from '../../../interoperability/hooks/useRenapo';
import toast from 'react-hot-toast';
import { TOAST_SUCCESS_IDS } from '../../../../interaces/toast.ids.interface';
import { useAuth } from '../../../../context/AuthContext';
import styles from '../../styles/styles.module.css';
const STATUS = {
  CHECKING: 'checking',
  VALID: 'valid',
  INVALID: 'invalid',
  EXPIRED: 'expired',
  SUCCESS: 'success',
};

function Onboarding() {
  const { user, getByCurp, setCurp, curp } = useRenapo();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState(STATUS.CHECKING);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);
  const [obscure, setObscure] = useState(true);
  const [obscureConfirm, setObscureConfirm] = useState(true);
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      otp: '',
      curp: '',
      rfc: '',
      password: '',
      confirmPassword: '',
    },
  });
  const formCurp = watch('curp');
  const validatedTokenRef = useRef(null);

  useEffect(() => {
    const normalizedCurp = formCurp?.trim().toUpperCase() || '';
    setCurp(normalizedCurp.length === 18 ? normalizedCurp : null);
  }, [formCurp, setCurp]);

  useEffect(() => {
    if (curp) getByCurp();
  }, [curp, getByCurp]);

  useEffect(() => {
    if (!token) {
      setStatus(STATUS.INVALID);
      setErrorMessage('No se proporcionó un token de invitación en el enlace.');
      return;
    }

    if (validatedTokenRef.current === token) {
      return;
    }
    validatedTokenRef.current = token;

    const validate = async () => {
      setStatus(STATUS.CHECKING);
      try {
        const res = await onboardingApi.validateToken(token);
        setStatus(STATUS.VALID);
        toast.success(res?.message || 'Código OTP enviado a tu correo electrónico.');
      } catch (err) {
        const msg = err.message || '';
        if (msg.toLowerCase().includes('expir') || msg.toLowerCase().includes('utiliz')) {
          setStatus(STATUS.EXPIRED);
          setErrorMessage(msg || 'La invitación ha expirado o ya fue utilizada.');
        } else {
          setStatus(STATUS.INVALID);
          setErrorMessage(msg || 'El enlace de invitación no es válido.');
        }
      }
    };

    validate();
  }, [token]);

  const handleResendOtp = async () => {
    if (!token || resendingOtp) return;
    setResendingOtp(true);
    try {
      const res = await onboardingApi.validateToken(token);
      toast.success(res?.message || 'Se ha reenviado un nuevo código OTP a tu correo.');
    } catch (err) {
      toast.error(err.message || 'Error al reenviar el código OTP.');
    } finally {
      setResendingOtp(false);
    }
  };

  const handleVerify = async ({ otp, curp, rfc, password }) => {
    try {
      const response = await onboardingApi.verify({
        token,
        otp,
        curp,
        rfc: rfc || undefined,
        password,
      });
      login(
        {
          username: response.username,
          role: response.role,
          fullName: response.fullName,
          privileges: response.privileges || [],
        },
        response.token
      );
      navigate('/home');
      setStatus(STATUS.SUCCESS);
      toast.success(TOAST_SUCCESS_IDS.SUCCESFUL_REGISTRATION, {
        id: TOAST_SUCCESS_IDS.SUCCESFUL_REGISTRATION,
      });
    } catch (err) {
      const msg = err.message || 'Error al completar el registro.';
      setError('root.server', { message: msg });
      toast.error(msg);
    }
  };


// ── Validando invitación ────────────────────────────────────────────────
if (status === STATUS.CHECKING) {
  return (
    <div className="main-div">
      <div className="secondary-div">
        <div className="panel-header">
          <FaShieldAlt className="panel-header-icon" />
          <h1 className="panel-header-title">
            VALIDANDO INVITACIÓN...
          </h1>
        </div>

        <div className={`panel-body ${styles.statusBody}`}>
          <p className={styles.statusMessage}>
            Por favor espera...
          </p>
        </div>
      </div>
    </div>
  );
}
if (status === STATUS.EXPIRED) {
  return (
    <div className="main-div">
      <div className="secondary-div">
        <div className={`panel-header ${styles.expiredHeader}`}>
          <FaClock className="panel-header-icon" />
          <h1 className="panel-header-title">
            INVITACIÓN EXPIRADA
          </h1>
        </div>

        <div className={`panel-body ${styles.statusBody}`}>
          <p className={styles.statusMessage}>
            {errorMessage ||
              'Este enlace de invitación ya no es válido o ha expirado.'}
            <br />
            Por favor solicita a tu administrador que genere una nueva invitación.
          </p>

          <button
            type="button"
            className={`btn-primary ${styles.statusButton}`}
            onClick={() => navigate('/')}
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
}

if (status === STATUS.INVALID) {
  return (
    <div className="main-div">
      <div className="secondary-div">
        <div className={`panel-header ${styles.invalidHeader}`}>
          <FaExclamationTriangle className="panel-header-icon" />
          <h1 className="panel-header-title">
            ENLACE NO VÁLIDO
          </h1>
        </div>

        <div className={`panel-body ${styles.statusBody}`}>
          <p className={styles.statusMessage}>
            {errorMessage ||
              'El enlace de invitación no es válido o no existe en el sistema.'}
          </p>

          <button
            type="button"
            className={`btn-primary ${styles.statusButton}`}
            onClick={() => navigate('/')}
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
}

if (status === STATUS.SUCCESS) {
  return (
    <div className="main-div">
      <div className="secondary-div">
        <div className={`panel-header ${styles.successHeader}`}>
          <FaCheckCircle className="panel-header-icon" />
          <h1 className="panel-header-title">
            ¡REGISTRO COMPLETADO!
          </h1>
        </div>

        <div className={`panel-body ${styles.statusBody}`}>
          <p className={styles.successMessage}>
            Tu cuenta ha sido configurada y activada correctamente en la plataforma.
            <br />
            Serás redirigido al inicio de sesión en unos momentos...
          </p>

        </div>
      </div>
    </div>
  );
}



return (
  <div className="main-div">
    <div className="secondary-div">
      <div className="panel-header">
        <FaShieldAlt className="panel-header-icon" />
        <h1 className="panel-header-title">
          REGISTRO DE USUARIOS
        </h1>
      </div>

      <div className="panel-body">
        <p className={styles.introText}>
          Se ha enviado un código de verificación (OTP) a tu correo.
          Es válido por 15 minutos.
        </p>

        <p className={styles.introText}>
          Tu nombre de usuario es el email al que te llegó este enlace.
        </p>

        {errors.root?.server && (
          <div className={styles.serverError}>
            {errors.root.server.message}
          </div>
        )}

        <form
          className="form-modal"
          onSubmit={handleSubmit(handleVerify)}
        >
          {/* Código OTP */}
          <label className="label-input">
            Código OTP (6 dígitos)
          </label>

          <div className="input-inline-login">
            <FaKey className="icon-input" />

            <input
              className="input-plain-login"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Ej. 123456"
              maxLength={6}
              {...register('otp', {
                onChange: (event) => {
                  event.target.value = event.target.value.replace(/\D/g, '');
                },
              })}
            />
          </div>

          {errors.otp && (
            <span className="error-text">
              {errors.otp.message}
            </span>
          )}

          {/* CURP */}
          <label className={`label-input ${styles.fieldLabel}`}>
            CURP
          </label>

          <div className="input-inline-login">
            <FaIdCard className="icon-input" />

            <input
              className="input-plain-login"
              type="text"
              autoComplete="off"
              placeholder="18 caracteres"
              maxLength={18}
              {...register('curp', {
                onChange: (event) => {
                  event.target.value = event.target.value.toUpperCase();
                },
              })}
            />
          </div>

          {errors.curp && (
            <span className="error-text">
              {errors.curp.message}
            </span>
          )}

          {/* RFC */}
          <label className={`label-input ${styles.fieldLabel}`}>
            RFC
          </label>

          <div className="input-inline-login">
            <FaIdCard className="icon-input" />

            <input
              className="input-plain-login"
              type="text"
              autoComplete="off"
              placeholder="RFC"
              maxLength={18}
              {...register('rfc', {
                onChange: (event) => {
                  event.target.value = event.target.value.toUpperCase();
                },
              })}
            />
          </div>

          {errors.rfc && (
            <span className="error-text">
              {errors.rfc.message}
            </span>
          )}

          {/* Datos personales */}
          <div className={styles.identityFields}>
            <label className="label-input">
              Nombre(s)
            </label>

            <p className={styles.identityValue}>
              {curp === formCurp && user
                ? user.name
                : 'Se consultará con RENAPO al ingresar la CURP.'}
            </p>

            <label className={`label-input ${styles.identityLabel}`}>
              Primer apellido
            </label>

            <p className={styles.identityValue}>
              {curp === formCurp && user
                ? user.firstLastName
                : '—'}
            </p>

            <label className={`label-input ${styles.identityLabel}`}>
              Segundo apellido
            </label>

            <p className={styles.identityValue}>
              {curp === formCurp && user
                ? user.secondLastName
                : '—'}
            </p>
          </div>

          {/* Contraseña nueva */}
          <label className={`label-input ${styles.fieldLabel}`}>
            Contraseña nueva
          </label>

          <div className="password-inline-login">
            <FaLock className="icon-input" />

            <input
              className="input-plain-login"
              type={obscure ? 'password' : 'text'}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
              {...register('password')}
            />

            <button
              type="button"
              className={`eye-toggle ${styles.passwordToggle}`}
              onClick={() => setObscure(!obscure)}
              aria-label={
                obscure
                  ? 'Mostrar contraseña'
                  : 'Ocultar contraseña'
              }
              aria-pressed={!obscure}
            >
              {obscure ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {errors.password && (
            <span className="error-text">
              {errors.password.message}
            </span>
          )}

          {/* Confirmar contraseña */}
          <label className={`label-input ${styles.fieldLabel}`}>
            Confirmar contraseña
          </label>

          <div className="password-inline-login">
            <FaLock className="icon-input" />

            <input
              className="input-plain-login"
              type={obscureConfirm ? 'password' : 'text'}
              autoComplete="new-password"
              placeholder="Repite la contraseña"
              {...register('confirmPassword')}
            />

            <button
              type="button"
              className={`eye-toggle ${styles.passwordToggle}`}
              onClick={() => setObscureConfirm(!obscureConfirm)}
              aria-label={
                obscureConfirm
                  ? 'Mostrar contraseña'
                  : 'Ocultar contraseña'
              }
              aria-pressed={!obscureConfirm}
            >
              {obscureConfirm ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {errors.confirmPassword && (
            <span className="error-text">
              {errors.confirmPassword.message}
            </span>
          )}

          {/* Botón de registro */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`btn-primary ${styles.submitButton}`}
          >
            {isSubmitting
              ? 'Verificando y registrando...'
              : 'COMPLETAR REGISTRO'}
          </button>

          {/* Reenviar OTP */}
          <div className={styles.resendContainer}>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendingOtp || isSubmitting}
              className={styles.resendButton}
            >
              <FaRedo
                className={
                  resendingOtp ? styles.spinIcon : undefined
                }
              />

              {resendingOtp
                ? 'Reenviando...'
                : '¿No recibiste el código? Reenviar OTP'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);
}

export default Onboarding;
